import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  MouseSensor,
  PointerSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { RegistryItem } from "../types";

import CustomDragOverlay from "./CustomDragOverlay";

import { EntityType, getEntityNumericId } from "@/queries/folder-entities/types";

interface DragDropContextType {
  activeItem: RegistryItem | null;
  overId: string | null;
  isDragging: boolean;
  draggedOverFolderId: number | null;
  descendantFolderIds: ReadonlySet<number>;
}

interface FolderHierarchyEntry {
  id: number;
  folder_id?: number | null;
}

const computeDescendantFolderIds = (
  sourceFolderId: number,
  folders: readonly FolderHierarchyEntry[],
): Set<number> => {
  const childrenByParent = new Map<number, number[]>();
  folders.forEach(f => {
    const parent = f.folder_id ?? null;
    if (parent === null) {
      return;
    }
    if (!childrenByParent.has(parent)) {
      childrenByParent.set(parent, []);
    }
    childrenByParent.get(parent)!.push(f.id);
  });

  const descendants = new Set<number>();
  const stack: number[] = [sourceFolderId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    const children = childrenByParent.get(current) ?? [];
    children.forEach(childId => {
      if (!descendants.has(childId)) {
        descendants.add(childId);
        stack.push(childId);
      }
    });
  }
  return descendants;
};

const DragDropContext = createContext<DragDropContextType | undefined>(undefined);

export const useDragDrop = () => {
  const context = useContext(DragDropContext);
  if (!context) {
    throw new Error("useDragDrop must be used within a DragDropProvider");
  }
  return context;
};

interface DragDropProviderProps {
  children: React.ReactNode;
  onMove: (item: RegistryItem, targetFolderId: number | null) => Promise<void>;
  folderHierarchy?: readonly FolderHierarchyEntry[];
}

const EMPTY_DESCENDANT_SET: ReadonlySet<number> = new Set<number>();

export const DragDropProvider = ({ children, onMove, folderHierarchy }: DragDropProviderProps) => {
  const [activeItem, setActiveItem] = useState<RegistryItem | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [draggedOverFolderId, setDraggedOverFolderId] = useState<number | null>(null);
  const [descendantFolderIds, setDescendantFolderIds] =
    useState<ReadonlySet<number>>(EMPTY_DESCENDANT_SET);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const autoScrollRef = useRef<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const item = event.active.data.current?.item as RegistryItem | undefined;
      if (!item) {
        return;
      }

      setActiveItem(item);
      scrollContainerRef.current = null;

      if (item.type === EntityType.FOLDER && folderHierarchy) {
        const folderId = getEntityNumericId(item.id);
        setDescendantFolderIds(
          folderId === undefined
            ? EMPTY_DESCENDANT_SET
            : computeDescendantFolderIds(folderId, folderHierarchy),
        );
      } else {
        setDescendantFolderIds(EMPTY_DESCENDANT_SET);
      }

      document.body.style.cursor = "move";
      document.body.style.userSelect = "none";
      document.body.classList.add("dragging");

      const originalElement = document.querySelector(
        `[data-item-id="${item.id}"][data-item-type="${item.type}"]`,
      );
      if (originalElement) {
        originalElement.classList.add("dragging-source");
      }
    },
    [folderHierarchy],
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!activeItem) {
        return;
      }

      const findScrollContainer = () => {
        const source = document.querySelector(
          `[data-item-id="${activeItem.id}"][data-item-type="${activeItem.type}"]`,
        );
        const container = source?.closest("[data-scroll-container]");
        return (container as HTMLElement | null) ?? null;
      };

      const container = scrollContainerRef.current || findScrollContainer();
      if (container) {
        scrollContainerRef.current = container;

        const rect = container.getBoundingClientRect();
        const y = event.active.rect.current.translated?.top || 0;

        const SCROLL_ZONE = 100;
        const SCROLL_SPEED = 10;

        if (autoScrollRef.current) {
          cancelAnimationFrame(autoScrollRef.current);
          autoScrollRef.current = null;
        }

        if (y < rect.top + SCROLL_ZONE) {
          const scrollUp = () => {
            container.scrollTop -= SCROLL_SPEED;
            if (container.scrollTop > 0) {
              autoScrollRef.current = requestAnimationFrame(scrollUp);
            }
          };
          autoScrollRef.current = requestAnimationFrame(scrollUp);
        } else if (y > rect.bottom - SCROLL_ZONE) {
          const scrollDown = () => {
            container.scrollTop += SCROLL_SPEED;
            if (container.scrollTop < container.scrollHeight - container.clientHeight) {
              autoScrollRef.current = requestAnimationFrame(scrollDown);
            }
          };
          autoScrollRef.current = requestAnimationFrame(scrollDown);
        }
      }
    },
    [activeItem],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { over } = event;

      if (!activeItem) {
        setOverId(null);
        setDraggedOverFolderId(null);
        return;
      }

      if (!over) {
        setOverId(null);
        setDraggedOverFolderId(null);
        return;
      }

      const overItemId = over.id as string;
      setOverId(overItemId);

      if (overItemId.startsWith("folder-")) {
        const folderId = Number(overItemId.replace("folder-", ""));
        const activeFolderId =
          activeItem.type === EntityType.FOLDER ? getEntityNumericId(activeItem.id) : undefined;

        if (activeFolderId === folderId) {
          setDraggedOverFolderId(null);
          return;
        }

        if (activeItem.folder_id === folderId) {
          setDraggedOverFolderId(null);
          return;
        }

        if (descendantFolderIds.has(folderId)) {
          setDraggedOverFolderId(null);
          return;
        }

        setDraggedOverFolderId(folderId);
      } else if (overItemId === "root-drop-zone") {
        if (activeItem.folder_id !== null && activeItem.folder_id !== undefined) {
          setDraggedOverFolderId(-1);
        } else {
          setDraggedOverFolderId(null);
        }
      } else if (overItemId.startsWith("parent-area-")) {
        const parentFolderId = (over.data.current?.parentFolderId ?? null) as number | null;
        if (parentFolderId === (activeItem.folder_id ?? null)) {
          setDraggedOverFolderId(null);
        } else if (parentFolderId !== null && descendantFolderIds.has(parentFolderId)) {
          setDraggedOverFolderId(null);
        } else if (parentFolderId === null) {
          setDraggedOverFolderId(-1);
        } else {
          setDraggedOverFolderId(parentFolderId);
        }
      } else {
        setDraggedOverFolderId(null);
      }
    },
    [activeItem, descendantFolderIds],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { over } = event;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.classList.remove("dragging");

      if (activeItem) {
        const originalElement = document.querySelector(
          `[data-item-id="${activeItem.id}"][data-item-type="${activeItem.type}"]`,
        );
        if (originalElement) {
          originalElement.classList.remove("dragging-source");
        }
      }

      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
        autoScrollRef.current = null;
      }

      if (!activeItem) {
        setActiveItem(null);
        setOverId(null);
        setDraggedOverFolderId(null);
        setDescendantFolderIds(EMPTY_DESCENDANT_SET);
        return;
      }

      let shouldMove = false;
      let targetFolderId: number | null = null;
      const overItemId = over ? (over.id as string) : null;

      if (overItemId === "root-drop-zone") {
        if (activeItem.folder_id !== null && activeItem.folder_id !== undefined) {
          shouldMove = true;
          targetFolderId = null;
        }
      } else if (overItemId?.startsWith("folder-")) {
        const folderId = Number(overItemId.replace("folder-", ""));

        if (
          !isNaN(folderId) &&
          folderId !== activeItem.folder_id &&
          !(
            activeItem.type === EntityType.FOLDER && getEntityNumericId(activeItem.id) === folderId
          ) &&
          !descendantFolderIds.has(folderId)
        ) {
          shouldMove = true;
          targetFolderId = folderId;
        }
      } else if (overItemId?.startsWith("parent-area-")) {
        const parentFolderId = (over!.data.current?.parentFolderId ?? null) as number | null;
        if (
          parentFolderId !== (activeItem.folder_id ?? null) &&
          !(parentFolderId !== null && descendantFolderIds.has(parentFolderId))
        ) {
          shouldMove = true;
          targetFolderId = parentFolderId;
        }
      }

      if (shouldMove) {
        try {
          await onMove(activeItem, targetFolderId);
        } catch {
          // eslint-disable-next-line no-console
          console.error("Error handling drag end");
        }
      }

      setActiveItem(null);
      setOverId(null);
      setDraggedOverFolderId(null);
      setDescendantFolderIds(EMPTY_DESCENDANT_SET);
    },
    [activeItem, onMove, descendantFolderIds],
  );

  const handleDragCancel = useCallback(() => {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.body.classList.remove("dragging");

    if (activeItem) {
      const originalElement = document.querySelector(
        `[data-item-id="${activeItem.id}"][data-item-type="${activeItem.type}"]`,
      );
      if (originalElement) {
        originalElement.classList.remove("dragging-source");
      }
    }

    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current);
      autoScrollRef.current = null;
    }

    setActiveItem(null);
    setOverId(null);
    setDraggedOverFolderId(null);
    setDescendantFolderIds(EMPTY_DESCENDANT_SET);
  }, [activeItem]);

  useEffect(
    () => () => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      activeItem,
      overId,
      isDragging: activeItem !== null,
      draggedOverFolderId,
      descendantFolderIds,
    }),
    [activeItem, overId, draggedOverFolderId, descendantFolderIds],
  );

  return (
    <DragDropContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <CustomDragOverlay />
      </DndContext>
    </DragDropContext.Provider>
  );
};
